'use client';

import { useRef, useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import {
  makeStore,
  AppStore,
  setData,
  setUserTypeLoginStatus,
} from '@next-app/lib/store';
import InitialData from './utils/InitialData';
import headerFooterApiData from './utils/get-header-footer-data';
import deviceType from './utils/get-device-type';
import { selectIsGSAUser } from '@next-app/lib/features/InitSelectors';
import { isNotNullOrUndefined } from '@next-app/utils/common-utils';
import { LoginStatus } from './constants/constants';

export default function StoreProvider({
  initResp,
  Endeca_user_segments,
  Endeca_date,
  children,
}: Readonly<{
  initResp?: any;
  Endeca_user_segments?: string;
  Endeca_date?: string;
  children: React.ReactNode;
}>) {
  const storeRef = useRef<AppStore>();
  if (!storeRef.current) {
    // Create the store instance the first time this renders
    storeRef.current = makeStore();
  }

  return (
    <Provider store={storeRef.current}>
      <StoreProviderWithInit
        initResp={initResp}
        Endeca_user_segments={Endeca_user_segments}
        Endeca_date={Endeca_date}
      >
        {children}
      </StoreProviderWithInit>
    </Provider>
  );
}

export function StoreProviderWithInit({
  initResp,
  children,
  Endeca_user_segments,
  Endeca_date,
}: Readonly<{
  initResp?: any;
  Endeca_user_segments?: string;
  Endeca_date?: string;
  children: React.ReactNode;
}>) {
  const dispatch = useDispatch();
  const hasCalledRef = useRef(false); // Persistent ref to prevent multiple calls
  const isGSAUser = useSelector(selectIsGSAUser);
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && hasCalledRef.current) return; // Exit early if already called in development mode
    hasCalledRef.current = true; // Set the ref to true after the first call

    const getGSACookie = () => {
      const cookies = document.cookie.split('; ');
      for (const cookie of cookies) {
        const [key, val] = cookie.split('=');
        if (key == 'isGSAUser') {
          return decodeURIComponent(val);
        }
      }
      return 'false';
    };
    const isGSA = getGSACookie();
    let isFurniture = false;
    if (window) {
      const currentPath = window.location.pathname;
      const furnitureTabRegex = /^\/furniture\//;
      if (furnitureTabRegex.test(currentPath)) {
        isFurniture = true;
      }
    }
    InitialData.sessionInfoGetJson({
      endecaUserSegments: Endeca_user_segments,
      endecaDate: Endeca_date,
      ...(!Endeca_user_segments &&
        !Endeca_date &&
        window &&
        window.location.search && {
          searchParams: window.location.search,
        }),
      isGSA,
      isFurniture,
    }).then(async (sessionInfoData) => {
      if (
        !sessionInfoData ||
        !sessionInfoData.sessionInfo ||
        !sessionInfoData.sessionInfo.sessionInfo
      ) {
        return;
      }
      const { profile, userSegmentsMap } =
        sessionInfoData.sessionInfo.sessionInfo;
      if (profile?.id) {
        document.cookie = `profileId=${profile.id}; path=/; max-age=86400`;
      }
      const storeId = profile?.selectedStore?.id;
      const userSegmentCode = userSegmentsMap?.userSegmentCode;
      const gsaUser =
        (isNotNullOrUndefined(isGSAUser) && isGSAUser) ||
        profile?.gsaUser ||
        false;
      const headerFooter = await headerFooterApiData(
        storeId,
        userSegmentCode,
        gsaUser,
      );
      dispatch(
        setData({
          ...sessionInfoData,
          init: initResp,
          headerFooter,
          deviceType: deviceType(),
        }),
      );
      dispatch(
        setUserTypeLoginStatus({
          userType: profile?.gsaUser
            ? 'gsa'
            : profile?.loginStatus === LoginStatus.LOGGED_IN
              ? LoginStatus.LOGGED_IN
              : profile?.loginStatus === LoginStatus.RECOGNIZED
                ? LoginStatus.RECOGNIZED
                : LoginStatus.GUEST,
          loginStatus: profile?.loginStatus,
        }),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGSAUser]);

  return children;
}
